import { motion } from "framer-motion";
import {
  GraduationCap,
  Rocket,
  Award,
  Landmark,
  Quote,
  Microscope,
  Lightbulb,
  TrendingUp,
  Handshake,
  ExternalLink,
} from "lucide-react";

// Founder credentials — mirrors the AI.Prof brochure "Mind Behind the Mission" page.
const credentials = [
  {
    Icon: GraduationCap,
    title: "AI & Robotics Expert",
    desc: "Trained at Carnegie Mellon University (ranked #1 globally for CS & AI).",
  },
  {
    Icon: Rocket,
    title: "Co-Founder, Abridge",
    desc: "A $5.3 Billion AI unicorn (USA).",
  },
  {
    Icon: Award,
    title: "Forbes 30 Under 30",
    desc: "Healthcare · Global.",
  },
  {
    Icon: Landmark,
    title: "Guest Lecturer",
    desc: "Stanford University, CMU, and UPitt.",
  },
];

// Global AI ecosystem & engineering impact — brochure page 3.
const impact = [
  {
    Icon: Microscope,
    title: "Prolific AI Researcher",
    desc: "20+ peer-reviewed publications in deep learning, computer vision & NLP across elite venues (AAAI, ICASSP, ML4H).",
    accent: "from-aiblue to-sky-400",
  },
  {
    Icon: Lightbulb,
    title: "Foundational IP Architect",
    desc: "Holds multiple core US patents in NLP, multi-loop ML & automated intelligence.",
    accent: "from-aiorange to-amber-400",
  },
  {
    Icon: TrendingUp,
    title: "Strategic Investor",
    desc: "Active venture backer of 18+ AI startups & enterprises worldwide — shaping the next wave of global tech.",
    accent: "from-aiorange to-orange-400",
  },
  {
    Icon: Handshake,
    title: "Trusted Advisor",
    desc: "Strategic technical counsel to premier global AI venture funds and elite founders.",
    accent: "from-aiblue to-blue-400",
  },
];

export default function SmruFounder() {
  return (
    <section
      id="founder"
      className="relative overflow-hidden py-20 sm:py-28 bg-gradient-to-b from-white via-aisky/10 to-aipeach/20"
    >
      {/* soft accent blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="float-slow absolute -top-24 -right-24 w-96 h-96 rounded-full bg-aiblue/10 blur-3xl" />
        <div className="float-fast absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-aiorange/10 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] bg-aiorange/10 text-aiorange">
            The Mind Behind the Mission
          </span>
          <h2 className="mt-5 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-gray-900 leading-tight">
            He cofounded a $5.3 billion AI company.{" "}
            <span className="bg-gradient-to-r from-[#0052FF] to-[#FF6B00] bg-clip-text text-transparent">
              Now, he's re-engineering education.
            </span>
          </h2>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-[20rem_1fr] gap-10 lg:gap-12 items-start">
          {/* LEFT — photo + identity */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="mx-auto lg:mx-0 max-w-xs w-full"
          >
            {/* Photo with name + position overlaid at the bottom */}
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-black/5 bg-gray-50">
              <img
                src="/partners/smru/sandeepKonam.jpg"
                alt="Sandeep Konam — Founder & Chairman, AI.Prof"
                className="w-full h-auto object-cover"
              />
              {/* readability scrim */}
              <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                <p className="font-display font-black text-2xl leading-tight">
                  Sandeep Konam
                </p>
                <p className="text-sm font-semibold text-white/90">
                  Founder &amp; Chairman, AI.Prof
                </p>
                <p className="text-[13px] text-white/75">
                  Co-Founder, Abridge · KONAM Foundation
                </p>
              </div>
            </div>

            {/* Affiliation logos — Carnegie Mellon University × Abridge */}
            <div className="mt-4 grid grid-cols-2 divide-x divide-gray-200 rounded-2xl bg-white ring-1 ring-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-center justify-center py-6 px-4">
                <img
                  src="/CMU.png"
                  alt="Carnegie Mellon University"
                  className="h-14 w-auto object-contain"
                />
              </div>
              <div className="flex items-center justify-center py-6 px-4">
                <img
                  src="/ABRIDGE.png"
                  alt="Abridge"
                  className="h-16 sm:h-20 w-auto max-w-full object-contain"
                />
              </div>
            </div>
          </motion.div>

          {/* RIGHT — brochure bio + credentials + quote */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-gray-600 leading-relaxed text-[15px] sm:text-base">
              Born in rural{" "}
              <span className="font-semibold text-gray-900">Nalgonda</span>,
              Sandeep is living proof that exceptional talent exists universally,
              but elite opportunity does not. After building global enterprise AI
              at scale, he realized his ultimate milestone is no longer just
              inventing breakthrough technology — it is architecting the
              ecosystem that will build the next generation of engineers. He
              architected{" "}
              <span className="font-semibold text-aiblue">AI.Prof</span> to solve
              a glaring contradiction: a tech ecosystem aggressively raising the
              bar for talent, while brilliant young minds remain trapped in an
              obsolete education model.
            </p>

            {/* Credentials */}
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {credentials.map(({ Icon, title, desc }) => (
                <div
                  key={title}
                  className="flex items-start gap-3 rounded-2xl bg-white/70 ring-1 ring-gray-100 px-4 py-3.5 shadow-sm"
                >
                  <span className="flex-shrink-0 h-10 w-10 rounded-full bg-aiblue/10 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-aiblue" />
                  </span>
                  <span className="leading-snug">
                    <span className="block text-sm font-bold text-gray-900">
                      {title}
                    </span>
                    <span className="block text-[13px] text-gray-500">
                      {desc}
                    </span>
                  </span>
                </div>
              ))}
            </div>

            {/* Quote */}
            <div className="mt-7 relative rounded-2xl bg-white ring-1 ring-gray-100 shadow-sm p-6 sm:p-7">
              <span
                aria-hidden="true"
                className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-gradient-to-b from-aiblue to-aiorange"
              />
              <Quote className="h-7 w-7 text-aiorange/40 mb-2 -scale-x-100" />
              <p className="font-display text-lg sm:text-xl font-bold text-gray-900 leading-snug">
                Every student deserves not just an education — but a{" "}
                <span className="text-aiorange">
                  clear, unstoppable path to success.
                </span>{" "}
                That is not a mission statement. It is the reason AI.Prof was
                built.
              </p>
              <p className="mt-3 text-sm font-semibold text-gray-500">
                — Sandeep Konam, Founder &amp; Chairman, AI.Prof
              </p>
            </div>
          </motion.div>
        </div>

        {/* Global AI Ecosystem & Engineering Impact — brochure page 3 */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative mt-16 lg:mt-20 overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#050B2E] via-[#0A2665] to-[#0A1E52] p-7 sm:p-10 lg:p-12 shadow-2xl"
        >
          {/* glow accents */}
          <div className="pointer-events-none absolute -top-24 -left-16 w-80 h-80 rounded-full bg-blue-500/20 blur-[100px]" />
          <div className="pointer-events-none absolute -bottom-24 -right-16 w-80 h-80 rounded-full bg-orange-500/15 blur-[100px]" />

          <div className="relative">
            {/* eyebrow */}
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-aiorange" />
              <span className="text-amber-300 text-xs font-bold uppercase tracking-[0.2em]">
                Global AI Ecosystem &amp; Engineering Impact
              </span>
            </div>

            {/* impact cards */}
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {impact.map(({ Icon, title, desc, accent }) => (
                <div
                  key={title}
                  className="relative overflow-hidden rounded-2xl bg-white/[0.06] ring-1 ring-white/10 backdrop-blur-sm p-5 sm:p-6"
                >
                  <span
                    aria-hidden="true"
                    className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${accent}`}
                  />
                  <div className="flex items-start gap-3">
                    <span className={`flex-shrink-0 h-10 w-10 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-lg`}>
                      <Icon className="h-5 w-5 text-white" strokeWidth={2} />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-display font-bold text-white text-lg leading-snug">
                        {title}
                      </h3>
                      <p className="mt-1.5 text-sm text-blue-100/75 leading-relaxed">
                        {desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* KONAM Foundation block */}
           
          </div>
        </motion.div>
      </div>
    </section>
  );
}
